import type { PaginationResultDto } from '@bondsports/types';
import { useInfiniteQuery, type QueryKey } from '@tanstack/react-query';
import * as React from 'react';
import { getNextPage } from './use-debounced-infinite-query.helpers';
import { useDebouncedValue } from './use-debounced-value';

const DEFAULT_ITEMS_PER_PAGE = 10;
const DEFAULT_SEARCH_DEBOUNCE_MS = 250;
const DEFAULT_STALE_TIME_MS = 5 * 60 * 1000;
const DEFAULT_CACHE_TIME_MS = 10 * 60 * 1000;
const FIRST_PAGE = 1;

export interface IDebouncedInfiniteQueryPageInput {
	page: number;
	itemsPerPage: number;
	search?: string;
	filters?: Record<string, unknown>;
	signal?: AbortSignal;
}

export interface IUseDebouncedInfiniteQueryInput<TItem> {
	queryKey: QueryKey;
	apiMethod: (
		input: IDebouncedInfiniteQueryPageInput,
		options?: { signal?: AbortSignal }
	) => Promise<PaginationResultDto<TItem>>;
	search?: string;
	filters?: Record<string, unknown>;
	itemsPerPage?: number;
	enabled?: boolean;
	retry?: boolean | number;
	staleTime?: number;
	cacheTime?: number;
	searchDebounceMs?: number;
	onError?: (error: unknown) => void;
}

/**
 * Internal core shared by `useListPageData` and `useInfinitePagination`. Wraps `useInfiniteQuery`
 * with debounced search input and an auto-derived query key. Single React-Query glue layer keeps
 * the two public hooks from drifting.
 */
export function useDebouncedInfiniteQuery<TItem>({
	queryKey,
	apiMethod,
	search = '',
	filters,
	itemsPerPage = DEFAULT_ITEMS_PER_PAGE,
	enabled = true,
	retry = false,
	staleTime = DEFAULT_STALE_TIME_MS,
	cacheTime = DEFAULT_CACHE_TIME_MS,
	searchDebounceMs = DEFAULT_SEARCH_DEBOUNCE_MS,
	onError,
}: IUseDebouncedInfiniteQueryInput<TItem>) {
	const debouncedSearch = useDebouncedValue(search, searchDebounceMs);

	const fullQueryKey = React.useMemo<QueryKey>(
		() => [...queryKey, debouncedSearch, filters, itemsPerPage],
		[queryKey, debouncedSearch, filters, itemsPerPage]
	);

	const result = useInfiniteQuery<PaginationResultDto<TItem>, Error>({
		queryKey: fullQueryKey,
		queryFn: ({ pageParam = FIRST_PAGE, signal }) =>
			apiMethod(
				{
					page: pageParam as number,
					itemsPerPage,
					search: debouncedSearch || undefined,
					filters,
					signal,
				},
				{ signal }
			),
		getNextPageParam: getNextPage,
		enabled,
		retry,
		staleTime,
		cacheTime,
		onError,
	});

	return { ...result, debouncedSearch, itemsPerPage };
}
